import React, { useEffect, useState } from "react";
import { ClipboardList, Check, Lock, Clock, ShieldCheck } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";
import api from "../api";
import AvailabilityPill from "../components/AvailabilityPill";

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6 ${className}`}>{children}</div>;
}

const CL_STATUS = {
  completed: { label: "Completed", cls: "border-emerald-200 bg-emerald-50 text-emerald-700", Icon: Check },
  in_progress: { label: "In Progress", cls: "border-amber-200 bg-amber-50 text-amber-700", Icon: Clock },
  locked: { label: "Locked", cls: "border-slate-200 bg-slate-100 text-slate-500", Icon: Lock },
};

function OnboardingOverride({ applicant, onClose }) {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [reasons, setReasons] = useState({});
  const [busy, setBusy] = useState(null);

  const load = () => api.get(`/admin/onboarding/${applicant.id}`).then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  useEffect(() => { load(); }, [applicant.id]);

  const override = async (n, done) => {
    const reason = reasons[n] || "";
    if (done && !reason.trim()) { toast({ title: "Reason required", description: "Enter a reason before overriding.", variant: "destructive" }); return; }
    setBusy(n);
    try {
      await api.put(`/admin/onboarding/${applicant.id}/${n}`, { done, reason });
      toast({ title: done ? "Item marked complete" : "Override removed" });
      setReasons((p) => ({ ...p, [n]: "" }));
      await load();
    } catch (e) {
      toast({ title: "Override failed", description: e.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusy(null); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="onboarding-override-dialog">
        <DialogHeader>
          <DialogTitle>Onboarding — {applicant.first_name} {applicant.last_name}</DialogTitle>
          <DialogDescription>Items complete automatically from verified activity. Override is for offline steps only and is recorded with your name, the date, and a reason.</DialogDescription>
        </DialogHeader>
        {!data ? <div className="py-8 text-center text-sm text-slate-400">Loading…</div> : (
          <div className="space-y-2">
            {data.items.map((item) => {
              const s = CL_STATUS[item.status] || CL_STATUS.locked;
              const S = s.Icon;
              return (
                <div key={item.n} className="rounded-xl border border-slate-100 p-3" data-testid={`admin-checklist-item-${item.n}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-800">{item.n}. {item.title}</span>
                      {item.overridden && <span className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-700"><ShieldCheck className="h-3 w-3" /> Admin verified</span>}
                    </div>
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${s.cls}`} data-testid={`admin-checklist-status-${item.n}`}><S className="h-3 w-3" /> {s.label}</span>
                  </div>
                  {item.overridden ? (
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <span className="text-xs text-slate-400">Overridden by admin</span>
                      <Button size="sm" variant="outline" disabled={busy === item.n} onClick={() => override(item.n, false)} data-testid={`remove-override-${item.n}`}>Remove override</Button>
                    </div>
                  ) : item.status !== "completed" && (
                    <div className="mt-2 flex items-center gap-2">
                      <Textarea rows={1} placeholder="Reason for override (required)" value={reasons[item.n] || ""} onChange={(e) => setReasons((p) => ({ ...p, [item.n]: e.target.value }))} className="min-h-[38px] flex-1 text-xs" data-testid={`override-reason-${item.n}`} />
                      <Button size="sm" disabled={busy === item.n} onClick={() => override(item.n, true)} className="btn-primary-gradient border-0 text-white" data-testid={`override-btn-${item.n}`}>Mark complete</Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {data?.log?.length > 0 && (
          <div className="mt-4 border-t border-slate-100 pt-4" data-testid="override-history">
            <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Override history</h4>
            <div className="space-y-1.5">
              {data.log.map((h, i) => (
                <div key={i} className="flex items-start gap-2 text-xs text-slate-500" data-testid={`override-log-${i}`}>
                  <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-violet-500" />
                  <span>
                    <strong className="text-slate-700">{h.title}</strong> — {h.action} by {h.admin_email} · {new Date(h.at).toLocaleString()}
                    {h.reason ? <> · "{h.reason}"</> : null}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const FILTERS = [
  { key: "applicant", label: "Applicants" },
  { key: "supervisor", label: "Supervisors" },
  { key: "administrator", label: "Administrators" },
];

export default function Users() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState("applicant");
  const [managers, setManagers] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [overrideUser, setOverrideUser] = useState(null);
  const [assignFilter, setAssignFilter] = useState("all");

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  const loadAssignData = () => {
    api.get("/admin/managers").then((r) => setManagers(r.data)).catch(() => {});
    api.get("/admin/assignments").then((r) => {
      const map = {};
      r.data.forEach((a) => { map[a.applicant_id] = { id: a.assigned_user_id, name: a.assigned_name, role: a.assigned_role }; });
      setAssignments(map);
    }).catch(() => {});
  };
  useEffect(() => { load(); loadAssignData(); }, []);

  const changeRole = async (id, role) => {
    try {
      await api.patch(`/admin/users/${id}/role`, { role });
      toast({ title: "Role updated", description: `User is now ${role}.` });
      load();
      loadAssignData();
    } catch (err) {
      toast({ title: "Error", description: err.response?.data?.detail || "Failed", variant: "destructive" });
    }
  };

  const assignManager = async (applicantId, managerId) => {
    const mgr = managers.find((m) => m.id === managerId);
    try {
      await api.post("/admin/assignments", { applicant_id: applicantId, assigned_user_id: managerId === "none" ? null : managerId, assigned_role: mgr?.role });
      setAssignments((prev) => ({ ...prev, [applicantId]: managerId === "none" ? undefined : { id: managerId, name: mgr?.name, role: mgr?.role } }));
      toast({ title: managerId === "none" ? "Assignment removed" : "Assignment saved", description: managerId === "none" ? "Applicant is now unassigned." : `Assigned to ${mgr?.name} (${mgr?.role}).` });
    } catch (err) {
      toast({ title: "Error", description: err.response?.data?.detail || "Failed", variant: "destructive" });
    }
  };

  const norm = (r) => (r === "admin" ? "administrator" : r === "recruiter" ? "supervisor" : r || "applicant");
  const matchesAssign = (u) => {
    if (tab !== "applicant" || assignFilter === "all") return true;
    const a = assignments[u.id];
    if (assignFilter === "unassigned") return !a;
    if (assignFilter === "assigned") return !!a;
    if (assignFilter === "supervisor") return a?.role === "supervisor";
    if (assignFilter === "administrator") return a?.role === "administrator";
    return true;
  };
  const filtered = users.filter((u) => norm(u.role) === tab && matchesAssign(u));
  const counts = FILTERS.reduce((acc, f) => ({ ...acc, [f.key]: users.filter((u) => norm(u.role) === f.key).length }), {});

  return (
    <Card>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-slate-900">People management</h2>
          <p className="text-sm text-slate-500">Manage applicants, supervisors, and administrators. Each applicant has one manager — a supervisor or an administrator.</p>
        </div>
      </div>
      <Tabs value={tab} onValueChange={setTab} className="mt-4">
        <TabsList>
          {FILTERS.map((f) => (
            <TabsTrigger key={f.key} value={f.key}>{f.label} ({counts[f.key] || 0})</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {tab === "applicant" && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="assign-filter">
          {["all", "assigned", "unassigned", "supervisor", "administrator"].map((f) => (
            <button key={f} onClick={() => setAssignFilter(f)} data-testid={`assign-filter-${f}`}
              className={`rounded-full border px-3 py-1 text-xs font-semibold capitalize ${assignFilter === f ? "border-violet-600 bg-violet-50 text-violet-700" : "border-slate-200 text-slate-500"}`}>
              {f === "all" ? "All" : f}
            </button>
          ))}
        </div>
      )}
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
              <th className="py-2 pr-4">Name</th><th className="py-2 pr-4">Email</th><th className="py-2 pr-4">Onboarding</th>
              {tab === "applicant" && <th className="py-2 pr-4">Availability</th>}
              {tab === "applicant" && <th className="py-2 pr-4">Assigned To</th>}
              <th className="py-2 pr-4">Role</th>
              {tab === "applicant" && <th className="py-2 pr-4">Checklist</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} className="border-b border-slate-50">
                <td className="py-3 pr-4 font-medium text-slate-800">{u.first_name} {u.last_name}</td>
                <td className="py-3 pr-4 text-slate-600">{u.email}</td>
                <td className="py-3 pr-4 text-slate-600">{u.onboarding_percent}%</td>
                {tab === "applicant" && (
                  <td className="py-3 pr-4" data-testid={`user-availability-${u.id}`}><AvailabilityPill status={u.availability_status} /></td>
                )}
                {tab === "applicant" && (
                  <td className="py-3 pr-4">
                    <Select value={assignments[u.id]?.id || "none"} onValueChange={(v) => assignManager(u.id, v)}>
                      <SelectTrigger className="w-52" data-testid={`assign-manager-${u.id}`}><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Unassigned</SelectItem>
                        {managers.map((m) => (<SelectItem key={m.id} value={m.id}>{m.name} · {m.role === "administrator" ? "Admin" : "Supervisor"}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </td>
                )}
                <td className="py-3 pr-4">
                  <Select value={norm(u.role)} onValueChange={(v) => changeRole(u.id, v)} disabled={u.id === user?.id}>
                    <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="applicant">Applicant</SelectItem>
                      <SelectItem value="supervisor">Supervisor</SelectItem>
                      <SelectItem value="administrator">Administrator</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                {tab === "applicant" && (
                  <td className="py-3 pr-4">
                    <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setOverrideUser(u)} data-testid={`checklist-btn-${u.id}`}><ClipboardList className="h-4 w-4" /> View</Button>
                  </td>
                )}
              </tr>
            ))}
            {filtered.length === 0 && (<tr><td colSpan={tab === "applicant" ? 7 : 4} className="py-8 text-center text-slate-400">No users in this group.</td></tr>)}
          </tbody>
        </table>
      </div>
      {overrideUser && <OnboardingOverride applicant={overrideUser} onClose={() => { setOverrideUser(null); load(); }} />}
    </Card>
  );
}
